<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { useBrowserZoom } from '@/composables/useBrowserZoom';

const { t } = useI18n();
const { zoomPercentage, suggestZoomReset, shouldShowWarning, dismissGlobally } =
  useBrowserZoom();

const isDismissedTemporarily = ref(false);
const dontShowAgain = ref(false);

const showWarning = computed(() => {
  return shouldShowWarning.value && !isDismissedTemporarily.value;
});

const resetKey = computed(() => suggestZoomReset());

function dismissWarning() {
  if (dontShowAgain.value) {
    // Save globally to localStorage - will never show again
    dismissGlobally();
  }
  // Dismiss temporarily for this session
  isDismissedTemporarily.value = true;
}

// Watch the checkbox - when checked, immediately dismiss and save
watch(dontShowAgain, (isChecked) => {
  if (isChecked) {
    dismissWarning();
  }
});
</script>

<template>
  <transition name="slide-down">
    <div v-if="showWarning" class="zoom-warning-layer">
      <div class="zoom-warning-card">
        <div class="zoom-warning-icon">
          <UiIcon name="AlertTriangle" />
        </div>
        <div class="zoom-warning-content">
          <h4 class="zoom-warning-title">
            {{ t('zoomWarning.title') }}
          </h4>
          <p class="zoom-warning-message">
            {{ t('zoomWarning.message', { zoomLevel: zoomPercentage }) }}
          </p>
          <p class="zoom-warning-instruction">
            {{ t('zoomWarning.instruction', { key: resetKey }) }}
          </p>
          <div class="zoom-warning-actions">
            <label class="dont-show-again">
              <input v-model="dontShowAgain" type="checkbox" class="checkbox" />
              <span class="checkbox-label">{{
                t('zoomWarning.dontShowAgain')
              }}</span>
            </label>
          </div>
        </div>
        <UiButton
          variant="flat"
          class="zoom-warning-close"
          :aria-label="t('zoomWarning.dismiss')"
          :title="t('zoomWarning.dismiss')"
          @click="dismissWarning"
        >
          <UiIcon name="XIcon" class="close-icon" />
        </UiButton>
      </div>
    </div>
  </transition>
</template>

<style lang="scss" scoped>
.zoom-warning-layer {
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  width: 90%;
  max-width: 600px;
  pointer-events: none;
}

.zoom-warning-card {
  display: flex;
  align-items: flex-start;
  gap: var(--space-12);
  padding: var(--space-16) var(--space-20);
  background-color: var(--color-warning-50, #fff8e1);
  border: 1px solid var(--color-warning-300, #ffb74d);
  border-radius: var(--border-radius-8);
  box-shadow: var(--shadow-lg);
  pointer-events: auto;
}

.zoom-warning-icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-warning-600, #f57c00);
  font-size: var(--font-size-20);
}

.zoom-warning-content {
  flex: 1;
  min-width: 0;
}

.zoom-warning-title {
  margin: 0 0 var(--space-4) 0;
  font-size: var(--font-size-14);
  font-weight: var(--font-weight-bold);
  color: var(--color-gray-900, #1a1a1a);
  line-height: var(--line-height-ratio-140);
}

.zoom-warning-message {
  margin: 0 0 var(--space-8) 0;
  font-size: var(--font-size-13);
  color: var(--color-gray-700, #4a4a4a);
  line-height: var(--line-height-ratio-150);
}

.zoom-warning-instruction {
  margin: 0 0 var(--space-12) 0;
  font-size: var(--font-size-14);
  font-weight: var(--font-weight-bold);
  color: var(--color-gray-900, #1a1a1a);
  line-height: var(--line-height-ratio-150);
  padding: var(--space-10) 14px;
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: var(--border-radius-6);
  border-left: 3px solid var(--color-primary, #1976d2);
}

.zoom-warning-actions {
  margin: 0;
}

.dont-show-again {
  display: flex;
  align-items: center;
  gap: var(--space-8);
  cursor: pointer;
  user-select: none;

  .checkbox {
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: var(--color-primary, #1976d2);
  }

  .checkbox-label {
    font-size: var(--font-size-13);
    color: var(--color-gray-700, #616161);
    cursor: pointer;
  }

  &:hover .checkbox-label {
    color: var(--color-gray-900, #1a1a1a);
  }
}

.zoom-warning-close {
  flex-shrink: 0;
  min-width: auto;
  padding: var(--space-8);

  .close-icon {
    width: 20px;
    height: 20px;
  }
}

// Animations
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all var(--duration-slow) var(--ease-standard);
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(-20px);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-10px);
}

.slide-down-enter-to,
.slide-down-leave-from {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}
</style>
